import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircleIcon, XIcon } from 'lucide-react';
const videos = [{
  id: '71f4e2149a654b0c9f643e9205f2def1',
  title: 'Purchase Orders and Receiving',
  description: 'Create purchase orders, track vendor deliveries, and process receiving with quality inspections and inventory updates.'
}, {
  id: '408df635aa6b4d8e83bac62b61701c92',
  title: 'Deal Initiation and Project Creation',
  description: 'Initiate new deals, create project records, and set up the foundational structure for project management.'
}, {
  id: '191536141dae4f5c8eb363c768dc8b2c',
  title: 'Project Planning',
  description: 'Plan project timelines, allocate resources, define milestones, and establish deliverables for successful execution.'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
export function DemoVideosPage() {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedVideo]);
  return <div className="min-h-screen bg-navy text-white font-sans selection:bg-violet-electric/30 pt-32 pb-20" data-id="element-811">
      <div className="max-w-7xl mx-auto px-6" data-id="element-812">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-813">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-violet-electric text-sm font-semibold tracking-widest uppercase mb-6 block" data-id="element-814">

            Demo Videos
          </motion.span>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8" data-id="element-815">

            See Thruline in action
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/60 leading-relaxed mb-6" data-id="element-816">

            Watch quick demos of Thruline's core capabilities — from production
            tracking to quality management.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-sm text-white/40 italic" data-id="element-817">

            Note: This is a subset of our functionality.{' '}
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="text-violet-electric hover:underline" data-id="element-818">

              Contact us
            </a>{' '}
            for the full set.
          </motion.p>
        </div>

        {/* Video Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-4xl mx-auto" variants={stagger} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} data-id="element-819">

          {videos.map(video => <motion.button key={video.id} variants={fadeUp} onClick={() => setSelectedVideo(video.id)} className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.03] overflow-hidden hover:border-violet-electric/30 hover:bg-white/[0.05] transition-all duration-300 text-left" data-id="element-820">

              {/* Video Thumbnail with Embedded Preview */}
              <div className="relative aspect-video bg-white/[0.02] overflow-hidden" data-id="element-821">
                <iframe src={`https://www.loom.com/embed/${video.id}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true&autoplay=false`} frameBorder="0" className="w-full h-full pointer-events-none" style={{
              pointerEvents: 'none'
            }} data-id="element-822" />

                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" data-id="element-823" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20" data-id="element-824">
                  <div className="w-16 h-16 rounded-full bg-violet-electric/30 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-825">
                    <PlayCircleIcon className="w-8 h-8 text-white" data-id="element-826" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-827">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-violet-electric transition-colors" data-id="element-828">
                  {video.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed" data-id="element-829">
                  {video.description}
                </p>
              </div>
            </motion.button>)}
        </motion.div>
      </div>

      {/* Video Modal */}
      <AnimatePresence data-id="element-830">
        {selectedVideo && <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedVideo(null)} data-id="element-831" />


            {/* Modal */}
            <motion.div className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-832">

              <motion.div className="relative w-full max-w-5xl pointer-events-auto" initial={{
            scale: 0.95,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.25,
            ease: [0.16, 1, 0.3, 1]
          }} onClick={e => e.stopPropagation()} data-id="element-833">

                {/* Close button */}
                <button onClick={() => setSelectedVideo(null)} className="absolute -top-12 right-0 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" aria-label="Close video" data-id="element-834">

                  <XIcon className="w-6 h-6 text-white" data-id="element-835" />
                </button>

                {/* Video Container */}
                <div className="relative rounded-2xl overflow-hidden bg-navy border border-white/10 shadow-2xl" data-id="element-836">
                  <div className="aspect-video" data-id="element-837">
                    <iframe src={`https://www.loom.com/embed/${selectedVideo}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`} frameBorder="0" allowFullScreen className="w-full h-full" data-id="element-838" />

                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}